// La page de l'appli ouverte à côté de l'assistant : ce qu'elle montre, et l'objet dont elle
// est la fiche, tiré de son adresse. L'assistant (panneau ouvert sur toutes les pages) la
// reçoit avec chaque message ; l'interface en affiche le nom dans l'en-tête du panneau.

/** Objet dont une page est la fiche. */
export type TypeObjetPage =
  | 'tache'
  | 'piece'
  | 'echeance'
  | 'soin'
  | 'tiers'
  | 'entite'
  | 'transaction'
  | 'brouillon'
  | 'connexion'

export type PageAppli = {
  /** Adresse dans l'appli, paramètres compris (la vue choisie). */
  chemin: string
  libelle: string
  objet?: { type: TypeObjetPage; id: string }
  /** Comment l'assistant relit l'objet (outil du MCP et argument). */
  lecture?: string
}

type Modele = { motif: string; libelle: string; type?: TypeObjetPage; lecture?: string }

// `:id` : un identifiant UUID ; tout autre segment s'écrit tel quel.
const PAGES: Modele[] = [
  { motif: '/', libelle: 'Accueil' },
  { motif: '/taches', libelle: 'Liste des tâches' },
  {
    motif: '/taches/:id',
    libelle: 'Fiche d’une tâche',
    type: 'tache',
    lecture: 'outil fiche, objet tache',
  },
  { motif: '/echeancier', libelle: 'Échéancier' },
  {
    motif: '/echeances/:id',
    libelle: 'Fiche d’une échéance',
    type: 'echeance',
    lecture: 'outil fiche, objet echeance',
  },
  { motif: '/paiements', libelle: 'Paiements' },
  { motif: '/pieces', libelle: 'Liste des pièces' },
  {
    motif: '/pieces/:id',
    libelle: 'Fiche d’une pièce',
    type: 'piece',
    lecture: 'outil fiche, objet piece',
  },
  { motif: '/justificatifs', libelle: 'Justificatifs (connexions des cabinets)' },
  {
    motif: '/justificatifs/:id',
    libelle: 'Justificatifs d’une connexion de cabinet',
    type: 'connexion',
    lecture: 'outil collectes, connexion_id',
  },
  { motif: '/releves', libelle: 'Relevés bancaires' },
  {
    motif: '/rapprochements/:id',
    libelle: 'Rapprochement d’une opération bancaire',
    type: 'transaction',
    lecture: 'outil banque, vue propositions, transaction_id',
  },
  { motif: '/refacturations', libelle: 'Frais à refacturer' },
  { motif: '/boite-envoi', libelle: 'Boîte d’envoi' },
  {
    motif: '/boite-envoi/:id',
    libelle: 'Envoi préparé',
    type: 'brouillon',
    lecture: 'outil envois, brouillon_id',
  },
  { motif: '/envois', libelle: 'Mes envois (photos et fichiers)' },
  { motif: '/photo', libelle: 'Photo d’un courrier' },
  { motif: '/sante', libelle: 'Santé du foyer' },
  {
    motif: '/sante/soins/:id',
    libelle: 'Fiche d’un soin',
    type: 'soin',
    lecture: 'outil fiche, objet soin',
  },
  { motif: '/entites', libelle: 'Référentiel : entités' },
  {
    motif: '/entites/:id',
    libelle: 'Fiche d’une entité',
    type: 'entite',
    lecture: 'outil entite',
  },
  {
    motif: '/entites/:id/plan',
    libelle: 'Plan de classement d’une entité',
    type: 'entite',
    lecture: 'outil referentiel, plan de l’entité',
  },
  { motif: '/tiers', libelle: 'Référentiel : tiers' },
  {
    motif: '/tiers/:id',
    libelle: 'Fiche d’un tiers',
    type: 'tiers',
    lecture: 'outil fiche, objet tiers',
  },
  { motif: '/personnes', libelle: 'Référentiel : personnes' },
  { motif: '/circuits', libelle: 'Circuits des pièces' },
  { motif: '/acces-temporaires', libelle: 'Accès temporaires' },
  { motif: '/reglages', libelle: 'Réglages' },
  { motif: '/installer', libelle: 'Installer l’appli' },
  // Un défi (connexion, CAPTCHA) se franchit par son titulaire seul : rien à lire pour l'assistant.
  { motif: '/defi/:id', libelle: 'Défi d’une connexion (à franchir soi-même)' },
  { motif: '/autoriser', libelle: 'Autorisation d’un client MCP' },
]

const INCONNUE = 'Page de l’appli'

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function correspond(motif: string, segments: string[]): string | null | undefined {
  const attendus = motif.split('/').filter(Boolean)
  if (attendus.length !== segments.length) return undefined
  let id: string | null = null
  for (const [i, a] of attendus.entries()) {
    const s = segments[i]!
    if (a === ':id') {
      if (!UUID.test(s)) return undefined
      id = s.toLowerCase()
    } else if (a !== s) return undefined
  }
  return id
}

/** Ce que montre la page à cette adresse (chemin et paramètres, sans l'origine). */
export function decrirePage(chemin: string): PageAppli {
  const [base = '/'] = chemin.split(/[?#]/)
  const segments = base.split('/').filter(Boolean)
  for (const m of PAGES) {
    const id = correspond(m.motif, segments)
    if (id === undefined) continue
    return {
      chemin,
      libelle: m.libelle,
      ...(id && m.type && { objet: { type: m.type, id } }),
      ...(id && m.lecture && { lecture: m.lecture }),
    }
  }
  return { chemin, libelle: INCONNUE }
}

/** Adresse de la fiche d'une tâche ou d'une pièce. */
export function cheminDePage(objet: { type: 'tache' | 'piece'; id: string }): string {
  return `/${objet.type === 'tache' ? 'taches' : 'pieces'}/${objet.id}`
}

// Une adresse de page dans un texte : précédée d'un blanc, d'une parenthèse ou d'un guillemet.
const ADRESSE = /(^|[\s(«"'])(\/[a-z][a-z-]*(?:\/[^\s/?#)»"',;]+)*(?:\?[^\s)»"']*)?)/g

/**
 * Un texte coupé en morceaux et en liens vers les pages de l'appli qu'il nomme par leur
 * adresse (une ponctuation finale reste au texte). Une adresse inconnue reste du texte.
 */
export function decouperLiens(texte: string): ({ texte: string } | { chemin: string })[] {
  const morceaux: ({ texte: string } | { chemin: string })[] = []
  let depuis = 0
  for (const m of texte.matchAll(ADRESSE)) {
    const chemin = m[2]!.replace(/[.,;:!?]+$/, '')
    if (decrirePage(chemin).libelle === INCONNUE) continue
    const debut = m.index + m[1]!.length
    if (debut > depuis) morceaux.push({ texte: texte.slice(depuis, debut) })
    morceaux.push({ chemin })
    depuis = debut + chemin.length
  }
  if (depuis < texte.length) morceaux.push({ texte: texte.slice(depuis) })
  return morceaux
}
